// Privacy Meter — F2/F3 prototype extensions
// 1. F2 D4L2 — Hard-Stop What-If sheet
// 2. F2     — Auto Redact preview (lives inside the Hard-Stop sheet)
// 3. F3     — Risk Meter per-axis drill-down
// D5 certainty is fixed to risk color.
//
// The F2 composer (inline at-risk annotations, both D2 timing levels)
// lives in annotated-composer.jsx.

import { useState, useEffect, useRef } from 'react';
import { Icons, RiskBadge } from './ui-atoms.jsx';
import { RadarChart, riskAxisLabel } from './risk-meter.jsx';
import { severityToRiskTier } from '../../shared/risk-tiers.js';
import { useCopy } from './representation.jsx';

// ───────────────────────────────────────────────────────────
// Confidence - fixed D5 risk-color representation for one value.
// ───────────────────────────────────────────────────────────
export function Confidence({ value }) {
  return <RiskBadge level={severityToRiskTier(value)} />;
}

// ═══════════════════════════════════════════════════════════
// Auto-Redact — preview state used inside the Hard-Stop sheet
// ═══════════════════════════════════════════════════════════
export function RedactedPreview({
  draft,
  undone,
  onToggleSpan,
  onAcceptSpan,
  manualText,
  setManualText,
  defaultText,
  mode = 'redacted',
}) {
  const [isEditing, setIsEditing] = useState(false);
  const hasManualText = manualText !== null;

  return (
    <div
      style={{
        padding: '16px',
        background: hasManualText
          ? 'linear-gradient(145deg, var(--paper-2), var(--paper))'
          : 'var(--paper-2)',
        borderRadius: 16,
        fontSize: 14,
        lineHeight: 1.6,
        color: 'var(--ink)',
        border: '1px solid var(--line)',
        boxShadow: '0 4px 12px oklch(0% 0 0 / 0.03)',
        transition: 'all 200ms ease',
      }}
    >
      <div
        className="eyebrow"
        style={{
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>
          {hasManualText
            ? 'manually edited draft'
            : mode === 'redacted'
              ? 'auto-redacted draft'
              : 'your draft'}
        </span>
        {hasManualText && (
          <button
            onClick={() => {
              setManualText(null);
              setIsEditing(false);
            }}
            style={{
              background: 'var(--paper-3)',
              border: 'none',
              padding: '4px 8px',
              color: 'var(--ink-soft)',
              cursor: 'pointer',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              borderRadius: 6,
              fontWeight: 600,
              transition: 'background 200ms ease, color 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--indigo-soft)';
              e.currentTarget.style.color = 'var(--indigo)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--paper-3)';
              e.currentTarget.style.color = 'var(--ink-soft)';
            }}
          >
            reset to auto
          </button>
        )}
      </div>

      {isEditing ? (
        <textarea
          autoFocus
          value={hasManualText ? manualText : defaultText}
          onChange={(e) => setManualText(e.target.value)}
          onBlur={(e) => {
            if (e.target.value === defaultText) {
              setManualText(null);
            }
            setIsEditing(false);
          }}
          style={{
            width: '100%',
            minHeight: 100,
            background: 'var(--paper)',
            border: '2px solid var(--indigo-soft)',
            borderRadius: 12,
            padding: '12px 14px',
            fontFamily: 'inherit',
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--ink)',
            resize: 'vertical',
            outline: 'none',
            marginBottom: 10,
            boxShadow: '0 2px 8px oklch(45% 0.11 265 / 0.08) inset, 0 0 0 2px var(--indigo-soft)',
            transition: 'border-color 200ms, box-shadow 200ms',
          }}
        />
      ) : (
        <div
          onClick={() => {
            if (!hasManualText) setManualText(defaultText);
            setIsEditing(true);
          }}
          title="Click to edit manually"
          style={{
            marginBottom: 10,
            cursor: 'text',
            padding: '12px 14px',
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            transition: 'all 200ms ease',
            boxShadow: '0 2px 4px oklch(0% 0 0 / 0.02) inset',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--indigo-soft)';
            e.currentTarget.style.background = 'oklch(99% 0 0)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--line)';
            e.currentTarget.style.background = 'var(--paper)';
          }}
        >
          {hasManualText ? (
            <span>&quot;{manualText}&quot;</span>
          ) : (
            <span>
              &quot;
              {draft.spans.map((s, i) => {
                if (!s.risk) return <span key={i}>{s.t}</span>;

                if (mode === 'original') {
                  const tier = severityToRiskTier(s.inference?.confidence ?? 0);
                  const tint =
                    tier === 3
                      ? 'var(--risk-3-bg)'
                      : tier === 2
                        ? 'var(--risk-2-bg)'
                        : 'var(--risk-1-bg)';
                  const edge =
                    tier === 3
                      ? 'var(--risk-3-edge)'
                      : tier === 2
                        ? 'var(--risk-2-edge)'
                        : 'var(--risk-1-edge)';
                  return (
                    <span
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAcceptSpan?.(i);
                      }}
                      title="Click to apply auto-redact suggestion"
                      style={{
                        background: tint,
                        borderBottom: `1.5px dotted ${edge}`,
                        padding: '0 2px',
                        borderRadius: 3,
                        cursor: 'pointer',
                      }}
                    >
                      {s.t}
                    </span>
                  );
                }

                const isUndone = undone.has(i);
                if (isUndone) {
                  // restored — show original, marked as undone
                  return (
                    <span
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSpan?.(i);
                      }}
                      title="Click to restore redaction"
                      style={{
                        background: 'var(--paper-3)',
                        borderBottom: '1.5px dotted var(--ink-muted)',
                        padding: '0 2px',
                        borderRadius: 3,
                        cursor: 'pointer',
                      }}
                    >
                      {s.t}
                    </span>
                  );
                }
                return (
                  <span
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSpan?.(i);
                    }}
                    title="Click to undo redaction"
                    style={{ cursor: 'pointer' }}
                  >
                    <span
                      style={{
                        color: 'var(--ink-muted)',
                        textDecoration: 'line-through',
                        textDecorationColor: 'var(--ink-faint)',
                      }}
                    >
                      {s.t}
                    </span>
                    <span
                      style={{
                        background: 'var(--indigo-soft)',
                        color: 'var(--indigo)',
                        borderRadius: 3,
                        padding: '0 4px',
                        fontWeight: 500,
                        marginLeft: 3,
                      }}
                    >
                      {s.replacement}
                    </span>
                  </span>
                );
              })}
              &quot;
            </span>
          )}
        </div>
      )}

      {/* Per-span chips */}
      <div className="eyebrow" style={{ marginBottom: 6 }}>
        {hasManualText ? 'per-span control (disabled during manual edit)' : 'per-span control'}
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          opacity: hasManualText ? 0.5 : 1,
          pointerEvents: hasManualText ? 'none' : 'auto',
        }}
      >
        {draft.spans.map((s, i) => {
          if (!s.risk) return null;
          const isUndone = undone.has(i);
          return (
            <button
              key={i}
              onClick={() => onToggleSpan(i)}
              aria-pressed={!isUndone}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: isUndone ? 'var(--paper-3)' : 'var(--paper)',
                border: `1px solid ${isUndone ? 'var(--ink-faint)' : 'var(--indigo-soft)'}`,
                borderRadius: 999,
                padding: '4px 10px 4px 8px',
                fontFamily: 'inherit',
                fontSize: 11,
                color: isUndone ? 'var(--ink-soft)' : 'var(--indigo)',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: isUndone ? 'var(--ink-faint)' : 'var(--indigo)',
                }}
              />
              <span style={{ fontWeight: 500 }}>{s.inference?.trait || 'span'}</span>
              <span className="mono" style={{ fontSize: 9, opacity: 0.7, letterSpacing: '0.04em' }}>
                {isUndone ? 'restored · tap to redact' : 'redacted · tap to keep'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Shared inference list (used by the Hard-Stop sheet, and after redact)
// ═══════════════════════════════════════════════════════════
export function InferenceList({ items, allClear }) {
  if (allClear) {
    return (
      <div
        style={{
          padding: '14px 16px',
          textAlign: 'center',
          border: '1px dashed var(--risk-1-edge)',
          background: 'var(--risk-1-bg)',
          borderRadius: 12,
          color: 'var(--risk-1-text)',
        }}
      >
        <div className="eyebrow" style={{ color: 'var(--risk-1-text)', marginBottom: 4 }}>
          all clear
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.4 }}>
          Every flagged span is redacted. The reader can no longer infer the items below from this
          message.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((inf, i) => (
        <div
          key={i}
          style={{
            padding: '12px 14px',
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            opacity: inf._faded ? 0.45 : 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 4,
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              {inf.trait}
              {inf._faded && (
                <span
                  className="mono"
                  style={{
                    marginLeft: 6,
                    fontSize: 9,
                    color: 'var(--ink-muted)',
                    letterSpacing: '0.08em',
                  }}
                >
                  · DEFUSED
                </span>
              )}
            </span>
            <Confidence value={inf.confidence} />
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
            {inf.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 1. F2 D4L2 — Hard-Stop What-If Sheet (with Auto-Redact)
// ═══════════════════════════════════════════════════════════
//
// a11y notes:
//  - role=alertdialog, aria-modal=true, focus trapped on the sheet
//  - outside-tap is a no-op (does NOT close)
//  - grabber is decorative (no drag handler)
//  - "Modify message" remains the no-ack escape (per spec)
//  - Send path: requires tick of 'I understand' AND tap of confirm button.
//    The prior two-stage gate (Continue → second-stage Send) collapsed
//    to a single forced acknowledgment so the D4L1/D4L2 contrast is
//    "no interruption vs. one forced ack" rather than "popup with
//    one tap vs. popup with two taps". The send policy and arm map own this
//    experimental distinction.
//
export function HardStopSheet({
  draft,
  draftRecord,
  onModify,
  onSend,
  onAutoRedact,
  onAckToggled,
  onRedactionSpanToggle,
  onRestoreAll,
  onReevaluateDraft,
  // True once the session's message-sending window has closed (time/message
  // limit reached) while this sheet happens to be open — e.g. the
  // participant was still reading/deciding when the deadline passed.
  // "Back"/"Modify" and redaction editing stay available (reading/editing
  // is fine); only the actual send is cut off, mirroring the composer's
  // own disabled state so this sheet can't be used to send past the limit.
  sendDisabled = false,
}) {
  const [redacted, setRedacted] = useState(false);
  const [undone, setUndone] = useState(new Set()); // span indices restored
  const [manualText, setManualText] = useState(null);
  const [manualInferences, setManualInferences] = useState(null);
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [acked, setAcked] = useState(false);
  const dialogRef = useRef(null);
  // D3 representation registry (T14): the title is the routed F2 copy
  // site, so a future tone variant can reword the hard stop without
  // touching this component.
  const title = useCopy('f2_hardstop_title');

  // Focus trap: focus the dialog on mount; ESC is intercepted (no-op)
  useEffect(() => {
    const node = dialogRef.current;
    if (node) node.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  useEffect(() => {
    if (manualText === null) {
      setIsReevaluating(false);
      setManualInferences(null);
      return;
    }
    let isCurrent = true;
    const timer = setTimeout(async () => {
      if (onReevaluateDraft) {
        setIsReevaluating(true);
        try {
          const res = await onReevaluateDraft(manualText, {
            trigger: 'hard-edit',
            isCurrent: () => isCurrent,
          });
          if (isCurrent) setManualInferences(res.messageInferences);
        } catch (e) {
          console.error('[HardStopSheet] manual re-evaluate failed', e);
        } finally {
          if (isCurrent) setIsReevaluating(false);
        }
      }
    }, 500);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [manualText, onReevaluateDraft]);

  // Effective inferences after redact + undo. Each inference is paired
  // to a draft span by trait match so undoing a span restores exactly
  // the inference whose disclosure that span carried — not the one at
  // the same array index, which can drift if inference and span
  // ordering diverge.
  // Coerce defensively: a malformed preview response with a non-array
  // inferences field would otherwise throw inside the .map below, blanking
  // the forced-ack sheet mid-acknowledgment (no error boundary wraps it,
  // and a throw here strands the participant on a broken modal with their
  // message undelivered). An empty array renders the all-clear state.
  const allInfs = Array.isArray(draftRecord?.inferences) ? draftRecord.inferences : [];

  const isEditing = manualText !== null;
  const allClear = isEditing
    ? manualInferences !== null && manualInferences.length === 0
    : redacted && undone.size === 0;

  // Joined redacted text — passed to onSend when the participant
  // commits while in the redacted state. Mirrors the on-screen
  // RedactedPreview so what they see is what gets sent.
  const buildRedactedText = () =>
    draft.spans.map((s, i) => (s.risk && !undone.has(i) ? (s.replacement ?? s.t) : s.t)).join('');
  const spanIdxByTrait = {};
  draft.spans.forEach((s, i) => {
    if (s.inference) spanIdxByTrait[s.inference.trait] = i;
  });

  let visible = [];
  if (isEditing) {
    visible = manualInferences ?? [];
  } else {
    visible = redacted
      ? allInfs.map((inf) => {
          const spanIdx = spanIdxByTrait[inf.trait];
          const restored = spanIdx != null ? undone.has(spanIdx) : true;
          return { ...inf, _faded: !restored };
        })
      : allInfs;
  }

  return (
    <>
      {/* Overlay — does NOT close on tap */}
      <div
        className="sheet-overlay"
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="hardstop-title"
        aria-describedby="hardstop-desc"
        tabIndex={-1}
        className="sheet"
        style={{ maxHeight: '92%' }}
      >
        {/* Decorative grabber, no drag */}
        <div
          className="sheet-grabber"
          aria-hidden
          style={{ background: 'var(--ink-faint)', opacity: 0.6 }}
        />

        {/* Header — banner + subtitle removed (each duplicates signal
            already carried by the ack-checkbox and inference list). */}
        <div style={{ padding: '10px 20px 8px' }}>
          <div className="eyebrow" id="hardstop-desc" style={{ marginBottom: 4 }}>
            Pre-send · F2 · D4L2
          </div>
          <div id="hardstop-title" style={{ fontSize: 19, fontWeight: 600, lineHeight: 1.25 }}>
            {title}
          </div>
        </div>

        <div className="scroll" style={{ flex: 1, overflow: 'auto', padding: '0 20px 8px' }}>
          {/* Draft / redacted preview */}
          <div style={{ marginBottom: 12 }}>
            <RedactedPreview
              draft={draft}
              undone={undone}
              manualText={manualText}
              setManualText={setManualText}
              defaultText={redacted ? buildRedactedText() : draft.text}
              mode={redacted ? 'redacted' : 'original'}
              onToggleSpan={(i) => {
                const nextRedacted = undone.has(i);
                setUndone((u) => {
                  const n = new Set(u);
                  if (n.has(i)) n.delete(i);
                  else n.add(i);
                  return n;
                });
                onRedactionSpanToggle?.(i, nextRedacted);
              }}
              onAcceptSpan={(i) => {
                setRedacted(true);
                const nextUndone = new Set();
                draft.spans.forEach((s, idx) => {
                  if (s.risk && idx !== i) nextUndone.add(idx);
                });
                setUndone(nextUndone);
              }}
            />
          </div>

          {/* Inferences */}
          <div
            className="eyebrow"
            style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span>
              {redacted ? (allClear ? 'after redaction' : 'remaining inferences') : 'inferences'}
            </span>
            {isReevaluating && (
              <span style={{ fontSize: 9, opacity: 0.6, letterSpacing: '0.04em' }}>
                (updating...)
              </span>
            )}
          </div>
          <InferenceList items={visible} allClear={allClear} />

          {/* Acknowledge gate — single forced ack (no second stage) */}
          {!redacted && (
            <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'stretch' }}>
              <label
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  background: acked ? 'var(--indigo-soft)' : 'var(--paper-2)',
                  border: `1px solid ${acked ? 'var(--indigo)' : 'var(--line)'}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'background 140ms ease, border-color 140ms ease',
                }}
              >
                <span
                  role="checkbox"
                  aria-checked={acked}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      const next = !acked;
                      setAcked(next);
                      onAckToggled?.(next);
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    const next = !acked;
                    setAcked(next);
                    onAckToggled?.(next);
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: `1.5px solid ${acked ? 'var(--indigo)' : 'var(--ink-faint)'}`,
                    background: acked ? 'var(--indigo)' : 'var(--paper)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {acked && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12l5 5L20 7"
                        stroke="var(--paper)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--ink)' }}>
                  I understand the reader could draw these inferences from this message.
                </span>
              </label>
              <button
                className="btn btn-accent"
                style={{
                  flexShrink: 0,
                  padding: '0 12px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 500,
                }}
                onClick={() => {
                  setRedacted(true);
                  // Preserve any spans the participant restored while reviewing
                  // the original preview; Auto-redact applies only to the spans
                  // still selected for redaction. "Restore" below remains the
                  // explicit all-spans reset.
                  setManualText(null);
                  onAutoRedact?.();
                }}
              >
                Auto-redact
              </button>
            </div>
          )}
        </div>

        {/* Actions — button rows */}
        <div
          style={{
            padding: '12px 14px 22px',
            borderTop: '1px solid var(--line)',
            background: 'var(--paper-2)',
            display: 'flex',
            gap: 8,
          }}
        >
          {(() => {
            const currentText = isEditing
              ? manualText
              : redacted
                ? buildRedactedText()
                : draft.text;
            const hasChanges = currentText !== null && currentText !== draft.text;
            return (
              <button
                className="btn btn-ghost"
                style={{ flex: 1, padding: '13px 8px' }}
                onClick={() => onModify?.(hasChanges ? currentText : undefined)}
              >
                {hasChanges ? 'Save & back' : 'Back'}
              </button>
            );
          })()}
          {redacted && (
            <button
              className="btn btn-ghost"
              style={{ flex: 1, padding: '13px 8px' }}
              onClick={() => {
                setRedacted(false);
                setUndone(new Set());
                setManualText(null);
                onRestoreAll?.();
              }}
            >
              Restore
            </button>
          )}
          <button
            className="btn"
            disabled={(!redacted && !acked) || sendDisabled}
            onClick={() =>
              (redacted || acked) &&
              !sendDisabled &&
              onSend?.(
                redacted ? (manualText !== null ? manualText : buildRedactedText()) : undefined
              )
            }
            style={{
              flex: 1,
              padding: '13px 8px',
              background: (redacted || acked) && !sendDisabled ? 'var(--ink)' : 'var(--paper-3)',
              color: (redacted || acked) && !sendDisabled ? 'var(--paper)' : 'var(--ink-muted)',
              cursor: (redacted || acked) && !sendDisabled ? 'pointer' : 'not-allowed',
            }}
          >
            {sendDisabled ? 'Session ended' : redacted ? 'Send redacted' : 'Send anyway'}
          </button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// 3. F3 — Risk Meter per-axis drill-down
// ═══════════════════════════════════════════════════════════
//
// Decision: drill-down sits BELOW the radar (not replacing it) at ~40%
// of vertical real estate. The radar stays visible so you can switch
// axes without losing spatial context. Selected axis is the only one
// drawn in solid weight; the rest become hairlines so the focus is
// unmistakable.
//

export function MeterDrilldownSheet({
  meter,
  onClose,
  onBack,
  onJumpToMessage,
  axisEvidence = {},
  analysisUpdating = false,
  initialAxis,
  onAxisSelect,
  onEvidenceJump,
}) {
  const axisKeys = meter.map((row) => row.axis);
  const axisLabels = axisKeys.map(riskAxisLabel);
  // Lazy-init priority:
  //   1. initialAxis caller-provided (used when RiskMeterSheet
  //      hands off via "tap an axis · drill down") — only honored when
  //      it actually exists on the current meter.
  //   2. 'Location' when present (research-default starting axis).
  //   3. The first axis the meter carries.
  // Defensive fallback to the literal 'Location' string keeps missing
  // values readable on a malformed meter.
  const [selected, setSelected] = useState(() => {
    if (initialAxis && axisKeys.includes(initialAxis)) return initialAxis;
    if (axisKeys.includes('Location')) return 'Location';
    return axisKeys[0] ?? 'Location';
  });
  const meVals = meter.map((row) => row.meValue);
  const themVals = meter.map((row) => row.themValue);

  // F3 evidence is returned eagerly with the background meter pass. Expanding
  // an axis never creates another full-transcript request.
  const evidence = axisEvidence[selected] ?? {
    explanation: analysisUpdating
      ? 'Analysis is updating. Evidence will appear shortly.'
      : 'Evidence is unavailable right now.',
    contributing: [],
  };

  const selectedRow = meter.find((row) => row.axis === selected);
  const meVal = selectedRow?.meValue;
  const themVal = selectedRow?.themValue;
  return (
    <>
      <div className="sheet-overlay" onClick={onClose} />
      {/* Fixed height (not maxHeight): the contributing-messages list
          length varies per axis, and a content-driven sheet height made
          the frame jump up/down on every axis switch. The inner .scroll
          flex child absorbs the variable content instead. */}
      <div className="sheet" style={{ height: '94%', maxHeight: '94%' }}>
        <div className="sheet-grabber" />
        {/* Header */}
        <div
          style={{
            padding: '10px 20px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={onBack}
              aria-label="Back to risk meter"
              style={{
                background: 'var(--paper-2)',
                border: 'none',
                borderRadius: 999,
                width: 30,
                height: 30,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {Icons.back(16, 'var(--ink-soft)')}
            </button>
            <div>
              <div className="eyebrow">Risk Meter · F3 · drill-down</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>
                How exposed are you?
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'var(--paper-2)',
              border: 'none',
              borderRadius: 999,
              width: 30,
              height: 30,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {Icons.close(14, 'var(--ink-soft)')}
          </button>
        </div>

        <div className="scroll" style={{ flex: 1, overflow: 'auto' }}>
          {/* Radar */}
          <div style={{ display: 'grid', placeItems: 'center', padding: '0 8px' }}>
            <RadarChart
              axes={axisKeys}
              axisLabels={axisLabels}
              meValues={meVals}
              themValues={themVals}
              selected={selected}
              onSelectAxis={(axis) => {
                setSelected(axis);
                onAxisSelect?.(axis);
              }}
              size={260}
            />
          </div>
          <div
            className="mono"
            style={{
              fontSize: 9,
              color: 'var(--ink-muted)',
              textAlign: 'center',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginTop: -4,
            }}
          >
            tap an axis label to drill down
          </div>

          {/* Drill-down panel */}
          <div
            style={{
              margin: '12px 16px 8px',
              background: 'var(--paper)',
              border: '1px solid var(--indigo-soft)',
              borderRadius: 14,
              overflow: 'hidden',
            }}
          >
            {/* Selected axis header */}
            <div
              style={{
                padding: '12px 14px',
                background: 'var(--indigo-soft)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="eyebrow" style={{ color: 'var(--indigo)' }}>
                  selected axis
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginTop: 2 }}>
                  {riskAxisLabel(selected)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                <div
                  style={{
                    textAlign: 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4,
                  }}
                >
                  <div
                    className="mono"
                    style={{
                      fontSize: 9,
                      color: 'var(--ink-muted)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    you
                  </div>
                  <div>
                    {typeof meVal !== 'number' ? (
                      '-'
                    ) : (
                      <RiskBadge level={severityToRiskTier(meVal)} />
                    )}
                  </div>
                </div>
                <div
                  style={{
                    textAlign: 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4,
                  }}
                >
                  <div
                    className="mono"
                    style={{
                      fontSize: 9,
                      color: 'var(--ink-muted)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    them
                  </div>
                  <div>
                    {typeof themVal !== 'number' ? (
                      '-'
                    ) : (
                      <RiskBadge level={severityToRiskTier(themVal)} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Explainer line */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
                {evidence.explanation}
              </div>
            </div>

            {/* Contributing messages */}
            <div style={{ padding: '10px 14px 14px' }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                contributing messages · {evidence.contributing.length}
              </div>
              {evidence.contributing.length === 0 ? (
                <div
                  style={{
                    padding: 14,
                    fontSize: 12,
                    color: 'var(--ink-muted)',
                    textAlign: 'center',
                    background: 'var(--paper-2)',
                    borderRadius: 10,
                    border: '1px dashed var(--line)',
                  }}
                >
                  No messages have leaked this trait yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {evidence.contributing.map((it, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        onEvidenceJump?.(selected, it.msgId);
                        onJumpToMessage?.(it.msgId);
                      }}
                      style={{
                        textAlign: 'left',
                        cursor: 'pointer',
                        background: 'var(--paper-2)',
                        border: '1px solid var(--line)',
                        borderRadius: 10,
                        padding: '10px 12px',
                        display: 'grid',
                        gridTemplateColumns: '32px 1fr auto',
                        alignItems: 'flex-start',
                        gap: 10,
                        fontFamily: 'inherit',
                      }}
                    >
                      <span
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: 'var(--ink-muted)',
                          background: 'var(--paper)',
                          padding: '2px 0',
                          borderRadius: 4,
                          textAlign: 'center',
                          marginTop: 1,
                        }}
                      >
                        #{it.msgId}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            fontSize: 13,
                            lineHeight: 1.4,
                            display: 'block',
                            color: 'var(--ink)',
                          }}
                        >
                          &quot;{it.snippet}&quot;
                        </span>
                        <span
                          style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}
                        >
                          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-soft)' }}>
                            {it.trait}
                          </span>
                          <Confidence value={it.confidence} />
                        </span>
                      </span>
                      <span
                        className="mono"
                        style={{
                          fontSize: 9,
                          color: 'var(--indigo)',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        jump {Icons.chevron(11, 'currentColor')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className="mono"
            style={{
              margin: '0 16px 24px',
              fontSize: 10,
              color: 'var(--ink-muted)',
              textAlign: 'center',
              letterSpacing: '0.06em',
            }}
          >
            tap any axis label on the radar to switch — radar stays visible to keep spatial context
          </div>
        </div>
      </div>
    </>
  );
}
